'use client';
import {
  resolveTranslations,
  type PresentingLocale,
} from '@mentimeter/i18n-core';
import { messages as ragnarMessages } from '@mentimeter/i18n-ragnar';
import { getPresentingLocale } from '@mentimeter/i18n-presenting/getPresentingLocale';
import { usePrevious } from '@mentimeter/react-hooks';
import { NextIntlClientProvider, type AbstractIntlMessages } from 'next-intl';
import * as React from 'react';
import { messages as participatingMessages } from '../index';

/**
 * This component is used to provide translations for the part of the voting app
 * where a participant has joined a presentation.
 *
 * Since it will receve all the messages from the server on the initial render, it only needs
 * to fetch new translations when the locale changes.
 *
 * This is important for performance reasons, as fetching translations can be
 * expensive.
 */

export const NextIntlParticipatingProvider = ({
  messages,
  locale,
  children,
}: {
  messages: AbstractIntlMessages;
  locale: string;
  children: React.ReactNode;
}) => {
  const [currentMessages, setCurrentMessages] = React.useState(messages);
  const currentPresentingLocale = getPresentingLocale(locale);
  const prevLocale = usePrevious<PresentingLocale>(currentPresentingLocale);

  React.useEffect(() => {
    if (prevLocale === undefined || prevLocale === currentPresentingLocale) {
      return;
    }
    const messages = resolveTranslations<PresentingLocale>(
      currentPresentingLocale,
      participatingMessages,
      ragnarMessages,
    );
    setCurrentMessages(messages);
  }, [locale, currentPresentingLocale, prevLocale]);

  return (
    <NextIntlClientProvider
      locale={currentPresentingLocale}
      messages={currentMessages}
    >
      {children}
    </NextIntlClientProvider>
  );
};
